import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { WithRouter } from '@/components/WithRouter'
import PageHeader from '@/components/PageHeader'
import { Card, Row, Col, Button, Slider, Space, message } from 'antd'
import { 
  SoundOutlined, 
  SoundFilled,
  UpOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateRightOutlined
} from '@ant-design/icons'
import * as EZUIKitModule from 'ezuikit-js'
import { fetchDeviceSupportAlgorithms } from '@/redux/ys7/algorithmSlice'
import { startDevicePtz, stopDevicePtz } from '@/service/ys7'

import './ServiceAlertsList.less'

const breadcrumbList = [
  { breadcrumbName: 'dashboard', href: '/client/commandCenter' },
  { breadcrumbName: '视频查看' }
]

class VideoProfile extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      deviceSerial: '',
      channelNo: 1,
      volume: 0, // 音量 0-100
      isSoundOn: false, // 声音开关
      playerReady: false, // 播放器是否就绪
      isRotated: true, // 是否已翻转180度（默认不翻转）
      isZoomEnabled: false, // 电子放大是否已开启
      zoomLevel: 0 // 当前放大倍数（用于显示）
    }
    this.videoPlayer = null // 视频播放器实例
    this.videoContainerRef = React.createRef() // 视频容器引用（参考 Page.js）
    this._isMounted = false // 标记组件是否已挂载
    this._cleanupTimer = null // 清理定时器
    this._zoomEventHandlers = null // 电子放大事件处理器
  }

  /**
   * 从 URL 参数获取设备信息
   */
  getDeviceInfoFromUrl = () => {
    const { location } = this.props
    if (!location || !location.search) {
      return { deviceSerial: '', channelNo: 1 }
    }

    // WithRouter 已经将 location.search 解析为对象
    const searchObj = location.search || {}
    const deviceSerial = searchObj.deviceSerial || ''
    const channelNo = parseInt(searchObj.channelNo, 10) || 1

    return { deviceSerial, channelNo }
  }

  componentDidMount() {
    this._isMounted = true
    
    // 从 URL 获取设备信息
    const { deviceSerial, channelNo } = this.getDeviceInfoFromUrl()
    
    if (!deviceSerial) {
      message.error('缺少设备序列号参数')
      return
    }

    // 通知其他窗口暂停播放器（避免资源冲突）
    this._notifyOtherWindowsToPause()

    this.setState({ deviceSerial, channelNo }, () => {
      // 延迟初始化，确保 token 已加载，并给其他窗口时间暂停播放器
      // 延迟时间稍长，确保 ServiceAlertsList 有时间暂停播放器
      setTimeout(() => {
        this.handleStartLiveVideo(deviceSerial, channelNo)
        // 调用查询设备支持算法列表接口
        this.fetchDeviceSupportAlgorithms(deviceSerial)
      }, 2000)
    })
  }

  /**
   * 通知其他窗口暂停播放器（避免资源冲突）
   */
  _notifyOtherWindowsToPause = () => {
    try {
      // 使用 localStorage 事件通知其他窗口
      localStorage.setItem('videoPlayerPauseRequest', Date.now().toString())
      
      // 也尝试使用 BroadcastChannel（如果支持）
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          const channel = new BroadcastChannel('videoPlayerControl')
          channel.postMessage({ type: 'pauseAll' })
          channel.close()
        } catch (e) {
          // BroadcastChannel 不支持，忽略
        }
      }
    } catch (e) {
      // 忽略通知失败
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    
    // 清除定时器
    if (this._cleanupTimer) {
      clearTimeout(this._cleanupTimer)
      this._cleanupTimer = null
    }
    
    // 清理视频播放器（卸载时不清空容器，让 React 自己处理）
    this.cleanupVideoPlayer(false)
  }

  /**
   * 清理视频播放器（参考 Page.js 的简单实现）
   * @param {boolean} clearContainer - 是否清空容器，卸载时为 false，重新初始化时为 true
   */
  cleanupVideoPlayer = async (clearContainer = false) => {
    // 先清理电子放大事件监听器
    this.cleanupZoomEventListeners()

    // 先停止云台控制（使用 API）
    if (this._currentPtzDirection !== null && this._currentPtzDirection !== undefined) {
      try {
        const { deviceSerial, channelNo } = this.state
        if (deviceSerial) {
          await stopDevicePtz({
            deviceSerial,
            channelNo: channelNo || 1,
            direction: this._currentPtzDirection
          })
        }
      } catch (e) {
        console.warn('清理时停止云台控制失败:', e)
      } finally {
        this._currentPtzDirection = null
      }
    }

    const { videoPlayer } = this
    if (videoPlayer) {
      try {
        // 关闭电子放大（如果已开启）
        try {
          if (videoPlayer.closeZoom && typeof videoPlayer.closeZoom === 'function') {
            await videoPlayer.closeZoom()
          }
        } catch (e) {
          console.warn('关闭电子放大失败:', e)
        }

        // 停止播放器
        if (videoPlayer.stop && typeof videoPlayer.stop === 'function') {
          try {
            videoPlayer.stop()
          } catch (e) {
            console.warn('停止播放器失败:', e)
          }
        }
        
        // 销毁播放器
        if (videoPlayer.destroy && typeof videoPlayer.destroy === 'function') {
          try {
            videoPlayer.destroy()
          } catch (e) {
            console.warn('销毁播放器失败:', e)
          }
        }
      } catch (e) {
        console.error('清理视频播放器失败:', e)
      } finally {
        this.videoPlayer = null
        if (this._isMounted) {
          this.setState({ 
            playerReady: false,
            isZoomEnabled: false,
            zoomLevel: 0
          })
        }
      }
    }
    
    // 只在重新初始化时清空容器，卸载时不清空（让 React 自己处理）
    if (clearContainer) {
      try {
        const container = this.videoContainerRef.current
        if (container && container.parentNode) {
          try {
            container.innerHTML = ''
          } catch (e) {
            console.warn('清空容器失败:', e)
          }
        }
      } catch (e) {
        console.warn('清理容器失败:', e)
      }
    }
  }

  /**
   * 开始播放实时视频（参考 Page.js 的简单实现）
   */
  handleStartLiveVideo = async (deviceSerial, channelNo = 1) => {
    const { app: { ys7Token: accessToken = '' } } = this.props

    // 先清理旧的播放器（重新初始化时清空容器）
    this.cleanupVideoPlayer(true)

    try {
      // 检查 EZUIKit 是否可用
      let EZUIKitClass = null

      if (window.EZUIKit && window.EZUIKit.EZUIKitPlayer) {
        EZUIKitClass = window.EZUIKit.EZUIKitPlayer
      } else if (EZUIKitModule?.default?.EZUIKitPlayer) {
        EZUIKitClass = EZUIKitModule.default.EZUIKitPlayer
        if (!window.EZUIKit) {
          window.EZUIKit = EZUIKitModule.default
        }
      } else if (EZUIKitModule?.EZUIKitPlayer) {
        EZUIKitClass = EZUIKitModule.EZUIKitPlayer
        if (!window.EZUIKit) {
          window.EZUIKit = { EZUIKitPlayer: EZUIKitModule.EZUIKitPlayer }
        }
      }

      if (!EZUIKitClass) {
        message.error('EZUIKit SDK 未正确加载，请刷新页面重试')
        return
      }

      // 等待容器渲染（参考 Page.js）
      setTimeout(() => {
        const container = this.videoContainerRef.current
        if (!container) {
          console.error('视频容器未找到')
          return
        }

        // 强制设置容器高度，防止被 flex 布局压缩
        container.style.height = '600px'
        container.style.minHeight = '600px'
        container.style.maxHeight = '600px'
        container.style.flexShrink = '0'

        // 清空容器
        container.innerHTML = ''

        // 生成 ezopen URL
        const ezopenUrl = `ezopen://open.ys7.com/${deviceSerial}/${channelNo || 1}.live`

        // 创建播放器实例（参考 Page.js 的配置）
        // 使用固定高度600px，确保播放器正确渲染
        const containerHeight = 600
        const player = new EZUIKitClass({
          id: 'video-profile-player',
          accessToken: accessToken,
          url: ezopenUrl,
          width: '100%',
          height: containerHeight, // 使用容器的实际高度（像素值），至少600px
          autoplay: true,
          template: 'simple', // 使用 simple 模板，更简洁
          plugin: ['ptz'], // 启用云台控制插件
          openSound: false, // 默认关闭声音（静音）
          streamInfoCBType: 1, // 不连续获取码流信息
          handleSuccess: () => {
            // 检查组件是否已挂载
            if (!this._isMounted) {
              return
            }
            
            try {
              // 隐藏码流信息
              if (player && typeof player.displayStreamInfo === 'function') {
                player.displayStreamInfo(false)
              }
              
              // 确保静音
              if (player && typeof player.openSound === 'function') {
                player.openSound(false)
              }

              // 根据 isRotated 状态决定初始化时是否应用旋转
              if (this._isMounted) {
                const { isRotated } = this.state
                // 延迟应用，确保 DOM 已完全渲染
                setTimeout(() => {
                  if (this._isMounted) {
                    this.applyVideoRotation(isRotated)
                  }
                }, 300)
              }
              
              // 设置电子放大事件监听（8.1.5+）
              this.setupZoomEventListeners(player)
              
              // 更新播放器状态（仅在组件挂载时）
              if (this._isMounted) {
                this.videoPlayer = player
                // 延迟更新 playerReady 状态，避免立即重新渲染影响 EZUIKit 的 DOM
                setTimeout(() => {
                  if (this._isMounted) {
                    this.setState({ 
                      playerReady: true,
                      volume: 0,
                      isSoundOn: false
                      // isRotated 保持初始值，不在这里修改
                    })
                  }
                }, 200)
                message.success('实时视频播放成功')
              }
            } catch (e) {
              console.warn('handleSuccess 执行失败:', e)
            }
          },
          handleError: (err) => {
            console.error('实时视频播放错误:', err)
            if (this._isMounted) {
              message.error(`视频播放失败: ${err.message || '未知错误'}`)
            }
          }
        })

        // 确保隐藏码流信息并应用旋转状态（延迟执行，参考 Page.js）
        this._cleanupTimer = setTimeout(() => {
          // 检查组件是否已挂载
          if (!this._isMounted) {
            return
          }
          
          try {
            if (player && typeof player.displayStreamInfo === 'function') {
              player.displayStreamInfo(false)
            }
            
            if (player && typeof player.openSound === 'function') {
              player.openSound(false)
            }
            
            // 再次确保旋转状态已应用（延迟执行，确保 DOM 完全渲染）
            if (this._isMounted) {
              const { isRotated } = this.state
              setTimeout(() => {
                if (this._isMounted) {
                  this.applyVideoRotation(isRotated)
                }
              }, 200)
            }
          } catch (e) {
            console.warn('延迟设置播放器属性失败:', e)
          }
        }, 500)
      }, 100)
    } catch (error) {
      console.error('启动实时视频播放失败:', error)
      message.error(`启动实时视频播放失败: ${error.message || '未知错误'}`)
    }
  }


  /**
   * 控制音量
   * 根据文档，使用 openSound() 和 closeSound() 方法控制声音
   * 注意：文档中提到默认音量是 0.8，暂时不支持更改音量大小
   */
  handleVolumeChange = (value) => {
    if (!this._isMounted) {
      return
    }
    
    this.setState({ volume: value, isSoundOn: value > 0 })
    
    if (!this.videoPlayer) {
      return
    }

    try {
      // 根据文档，使用 openSound() 和 closeSound() 方法
      // openSound() 返回 Promise，默认音量是 0.8
      if (value > 0) {
        if (this.videoPlayer.openSound && typeof this.videoPlayer.openSound === 'function') {
          this.videoPlayer.openSound().then(() => {
            // 开启声音成功
            if (this._isMounted) {
              this.setState({ isSoundOn: true })
            }
          }).catch((err) => {
            console.warn('开启声音失败:', err)
          })
        }
      } else {
        if (this.videoPlayer.closeSound && typeof this.videoPlayer.closeSound === 'function') {
          this.videoPlayer.closeSound().then(() => {
            // 关闭声音成功
            if (this._isMounted) {
              this.setState({ isSoundOn: false })
            }
          }).catch((err) => {
            console.warn('关闭声音失败:', err)
          })
        }
      }
      
      // 如果支持音量设置（可能是旧版本或扩展方法）
      if (this.videoPlayer.setVolume && typeof this.videoPlayer.setVolume === 'function') {
        try {
          this.videoPlayer.setVolume(value)
        } catch (e) {
          // 忽略错误，因为文档中没有这个方法
        }
      }
    } catch (error) {
      console.error('音量控制失败:', error)
    }
  }

  /**
   * 切换声音开关
   */
  handleSoundToggle = () => {
    const { isSoundOn, volume } = this.state
    const newSoundOn = !isSoundOn
    const newVolume = newSoundOn ? (volume || 50) : 0
    
    this.handleVolumeChange(newVolume)
  }

  /**
   * 初始化时修正画面方向
   * - 只旋转 video 元素，让画面变正（如果画面反转了）
   * - logo/时间叠加层（canvas）保持不动，因为它们已经是正确的
   */
  fixInitialVideoOrientation = () => {
    try {
      const playerElement = document.getElementById('video-profile-player')
      if (!playerElement) {
        console.warn('[fixInitialVideoOrientation] 播放器容器未找到')
        return
      }

      // 只对 video 元素做正向旋转，让反转的画面变正
      const videoElement = playerElement.querySelector('video')
      if (videoElement) {
        videoElement.style.transform = 'rotate(180deg)'
        videoElement.style.transformOrigin = 'center center'
        console.log('[fixInitialVideoOrientation] 已修正画面方向：video 旋转 180deg')
      } else {
        console.warn('[fixInitialVideoOrientation] video 元素未找到')
      }
    } catch (e) {
      console.warn('修正初始画面方向失败:', e)
    }
  }

  /**
   * 统一控制视频画面旋转
   * - 对整个容器做旋转，确保画面和 Logo/时间叠加层一起旋转，方向一致
   * - 清理内部 video/canvas 的 transform，避免叠加旋转
   * @param {boolean} rotated - 是否旋转 180 度
   */
  applyVideoRotation = (rotated = true) => {
    try {
      const playerElement = document.getElementById('video-profile-player')
      if (!playerElement) {
        console.warn('视频播放器容器未找到')
        return
      }

      const rotateValue = rotated ? 'rotate(180deg)' : 'rotate(0deg)'

      // 清理内部 video / canvas 上可能残留的 transform，避免叠加旋转
      const videoElement = playerElement.querySelector('video')
      if (videoElement) {
        videoElement.style.transform = ''
        videoElement.style.transformOrigin = ''
      }
      
      const canvasList = playerElement.querySelectorAll('canvas')
      canvasList.forEach(canvas => {
        canvas.style.transform = ''
        canvas.style.transformOrigin = ''
      })

      // 对整个容器做旋转，这样画面和 Logo/时间会一起旋转
      playerElement.style.transform = rotateValue
      playerElement.style.transformOrigin = 'center center'
      
      console.log(`[applyVideoRotation] 应用旋转: ${rotated ? '180度' : '0度'}`)
    } catch (e) {
      console.warn('应用视频旋转失败:', e)
    }
  }

  /**
   * 将方向字符串映射到 API 的 direction 数字
   * @param {string} direction - 方向：'up', 'down', 'left', 'right', 'zoomIn', 'zoomOut'
   * @returns {number} - API direction 值
   */
  mapDirectionToApi = (direction) => {
    const directionMap = {
      'up': 1,        // 上（修复：上下方向调转）
      'down': 0,      // 下（修复：上下方向调转）
      'left': 3,      // 左（修复：左右方向调转）
      'right': 2,     // 右（修复：左右方向调转）
      'zoomIn': 8,    // 物理放大
      'zoomOut': 9   // 物理缩小
    }
    return directionMap[direction] !== undefined ? directionMap[direction] : 0
  }

  /**
   * 将速度值（1-9）映射到 API 的 speed 值（0-2）
   * @param {number} speed - 速度：1-9，默认 5
   * @returns {number} - API speed 值：0-慢，1-适中，2-快
   */
  mapSpeedToApi = (speed = 5) => {
    if (speed <= 3) return 0  // 慢
    if (speed <= 6) return 1    // 适中
    return 2  // 快
  }

  /**
   * 云台控制（使用 API 接口）
   * @param {string} direction - 方向：'up', 'down', 'left', 'right', 'zoomIn', 'zoomOut'
   * @param {number} speed - 速度：1-9，默认 5
   */
  handlePTZControl = async (direction, speed = 5) => {
    if (!this._isMounted) {
      return
    }

    const { deviceSerial, channelNo } = this.state
    if (!deviceSerial) {
      message.warning('设备序列号不存在')
      return
    }

    try {
      const apiDirection = this.mapDirectionToApi(direction)
      const apiSpeed = this.mapSpeedToApi(speed)

      await startDevicePtz({
        deviceSerial,
        channelNo: channelNo || 1,
        direction: apiDirection,
        speed: apiSpeed
      })

      // 保存当前方向，用于停止时使用
      this._currentPtzDirection = apiDirection
    } catch (error) {
      console.error('云台控制失败:', error)
      const errorMsg = error?.response?.data?.msg || error?.message || '云台控制失败'
      message.error(errorMsg)
    }
  }

  /**
   * 停止云台控制（使用 API 接口）
   */
  handlePTZStop = async () => {
    if (!this._isMounted) {
      return
    }

    const { deviceSerial, channelNo } = this.state
    if (!deviceSerial) {
      return
    }

    try {
      await stopDevicePtz({
        deviceSerial,
        channelNo: channelNo || 1,
        direction: this._currentPtzDirection // 可选参数，传入当前方向
      })
      this._currentPtzDirection = null
    } catch (error) {
      console.error('停止云台控制失败:', error)
      // 停止失败不显示错误提示，避免干扰用户体验
    }
  }

  /**
   * 设置电子放大事件监听（8.1.5+）
   * @param {object} player - 播放器实例
   */
  setupZoomEventListeners = (player) => {
    if (!player || !player.eventEmitter) {
      return
    }

    try {
      // 检查是否有 EVENTS 对象
      const EZUIKitPlayer = window.EZUIKit?.EZUIKitPlayer || EZUIKitModule?.default?.EZUIKitPlayer || EZUIKitModule?.EZUIKitPlayer
      if (!EZUIKitPlayer || !EZUIKitPlayer.EVENTS) {
        console.warn('[VideoProfile] EZUIKitPlayer.EVENTS 不可用，跳过电子放大事件监听')
        return
      }

      // 监听电子放大变化事件
      const onZoomChangeHandler = (info) => {
        if (!this._isMounted) {
          return
        }
        try {
          // info: {"zoom": string, "reset"?: boolean}
          console.log('[VideoProfile] 电子放大变化:', info)
          // 可以根据需要更新状态显示当前放大级别
          // 注意：文档中没有明确说明 zoom 字段的具体格式，可能需要根据实际情况调整
        } catch (e) {
          console.warn('[VideoProfile] 处理电子放大变化事件失败:', e)
        }
      }

      // 监听打开电子放大事件
      const onOpenZoomHandler = () => {
        if (this._isMounted) {
          this.setState({ isZoomEnabled: true })
          console.log('[VideoProfile] 电子放大已开启')
        }
      }

      // 监听关闭电子放大事件
      const onCloseZoomHandler = () => {
        if (this._isMounted) {
          this.setState({ isZoomEnabled: false, zoomLevel: 0 })
          console.log('[VideoProfile] 电子放大已关闭')
        }
      }

      // 注册事件监听
      player.eventEmitter.on(EZUIKitPlayer.EVENTS.zoom.openZoom, onOpenZoomHandler)
      player.eventEmitter.on(EZUIKitPlayer.EVENTS.zoom.closeZoom, onCloseZoomHandler)
      player.eventEmitter.on(EZUIKitPlayer.EVENTS.zoom.onZoomChange, onZoomChangeHandler)

      // 保存事件处理器引用，用于清理
      this._zoomEventHandlers = {
        player,
        handlers: {
          openZoom: onOpenZoomHandler,
          closeZoom: onCloseZoomHandler,
          onZoomChange: onZoomChangeHandler
        }
      }
    } catch (e) {
      console.warn('[VideoProfile] 设置电子放大事件监听失败:', e)
    }
  }

  /**
   * 清理电子放大事件监听
   */
  cleanupZoomEventListeners = () => {
    if (!this._zoomEventHandlers) {
      return
    }

    try {
      const { player, handlers } = this._zoomEventHandlers
      if (player && player.eventEmitter) {
        const EZUIKitPlayer = window.EZUIKit?.EZUIKitPlayer || EZUIKitModule?.default?.EZUIKitPlayer || EZUIKitModule?.EZUIKitPlayer
        if (EZUIKitPlayer && EZUIKitPlayer.EVENTS) {
          player.eventEmitter.off(EZUIKitPlayer.EVENTS.zoom.openZoom, handlers.openZoom)
          player.eventEmitter.off(EZUIKitPlayer.EVENTS.zoom.closeZoom, handlers.closeZoom)
          player.eventEmitter.off(EZUIKitPlayer.EVENTS.zoom.onZoomChange, handlers.onZoomChange)
        }
      }
    } catch (e) {
      console.warn('[VideoProfile] 清理电子放大事件监听失败:', e)
    } finally {
      this._zoomEventHandlers = null
    }
  }

  /**
   * 开启电子放大（根据文档 8.0.5+）
   */
  handleEnableZoom = async () => {
    if (!this._isMounted || !this.videoPlayer) {
      return
    }

    try {
      if (this.videoPlayer.enableZoom && typeof this.videoPlayer.enableZoom === 'function') {
        await this.videoPlayer.enableZoom()
        if (this._isMounted) {
          this.setState({ isZoomEnabled: true })
          message.success('电子放大已开启')
        }
      } else {
        message.warning('当前播放器版本不支持电子放大功能')
      }
    } catch (error) {
      console.error('开启电子放大失败:', error)
      message.error('开启电子放大失败')
    }
  }

  /**
   * 关闭电子放大（根据文档 8.0.5+）
   */
  handleCloseZoom = async () => {
    if (!this._isMounted || !this.videoPlayer) {
      return
    }

    try {
      if (this.videoPlayer.closeZoom && typeof this.videoPlayer.closeZoom === 'function') {
        await this.videoPlayer.closeZoom()
        if (this._isMounted) {
          this.setState({ isZoomEnabled: false, zoomLevel: 0 })
          message.success('电子放大已关闭')
        }
      } else {
        message.warning('当前播放器版本不支持电子放大功能')
      }
    } catch (error) {
      console.error('关闭电子放大失败:', error)
      message.error('关闭电子放大失败')
    }
  }

  /**
   * 电子放大执行放大（8.1.5+）
   * @param {number} scale - 放大倍数，默认 1
   */
  handleZoomAdd = async (scale = 1) => {
    if (!this._isMounted || !this.videoPlayer) {
      return
    }

    const { isZoomEnabled } = this.state
    if (!isZoomEnabled) {
      message.warning('请先开启电子放大')
      return
    }

    try {
      if (this.videoPlayer.zoomAdd && typeof this.videoPlayer.zoomAdd === 'function') {
        await this.videoPlayer.zoomAdd(scale)
        // 更新放大级别（注意：实际级别需要从事件回调中获取）
        if (this._isMounted) {
          this.setState(prevState => ({ zoomLevel: prevState.zoomLevel + scale }))
        }
      } else {
        message.warning('当前播放器版本不支持电子放大缩放功能（需要 8.1.5+）')
      }
    } catch (error) {
      console.error('电子放大失败:', error)
      message.error('电子放大失败')
    }
  }

  /**
   * 电子放大执行缩小（8.1.5+）
   * @param {number} scale - 缩小倍数，默认 1
   */
  handleZoomSub = async (scale = 1) => {
    if (!this._isMounted || !this.videoPlayer) {
      return
    }

    const { isZoomEnabled } = this.state
    if (!isZoomEnabled) {
      message.warning('请先开启电子放大')
      return
    }

    try {
      if (this.videoPlayer.zoomSub && typeof this.videoPlayer.zoomSub === 'function') {
        await this.videoPlayer.zoomSub(scale)
        // 更新放大级别（注意：实际级别需要从事件回调中获取）
        if (this._isMounted) {
          this.setState(prevState => ({ zoomLevel: Math.max(0, prevState.zoomLevel - scale) }))
        }
      } else {
        message.warning('当前播放器版本不支持电子放大缩放功能（需要 8.1.5+）')
      }
    } catch (error) {
      console.error('电子缩小失败:', error)
      message.error('电子缩小失败')
    }
  }

  /**
   * 查询设备支持算法列表（使用 Redux）
   * @param {string} deviceSerial - 设备序列号
   */
  fetchDeviceSupportAlgorithms = async (deviceSerial) => {
    if (!deviceSerial) {
      console.warn('[VideoProfile] 设备序列号为空，无法查询算法列表')
      return
    }

    const { dispatch } = this.props

    // 使用 Redux action 调用接口
    const result = await dispatch(fetchDeviceSupportAlgorithms({ deviceSerial }))
    
    // 直接打印结果
    console.log('[VideoProfile] 设备支持算法列表查询结果:', result)
    console.log('[VideoProfile] 完整响应数据:', JSON.stringify(result, null, 2))
  }

  /**
   * 一键180度翻转
   */
  handleRotateToggle = () => {
    if (!this._isMounted) {
      console.warn('[handleRotateToggle] 组件未挂载')
      return
    }

    const { isRotated } = this.state
    const newRotated = !isRotated

    console.log(`[handleRotateToggle] 当前状态: ${isRotated}, 新状态: ${newRotated}`)

    try {
      // 立即应用旋转
      this.applyVideoRotation(newRotated)
      
      // 更新状态
      this.setState({ isRotated: newRotated }, () => {
        console.log(`[handleRotateToggle] 状态已更新: ${this.state.isRotated}`)
        message.success(newRotated ? '已翻转180度' : '已恢复正常')
      })
    } catch (error) {
      console.error('翻转失败:', error)
      message.error('翻转失败')
    }
  }

  render() {
    const { deviceSerial, channelNo, volume, isSoundOn, playerReady, isRotated, isZoomEnabled, zoomLevel } = this.state

    // 设计系统颜色常量
    const colors = {
      primary: '#1a78a5',        // 主色 - 专业蓝
      primaryHover: '#4da5c2',  // 主色悬停
      textPrimary: '#262626',   // 主要文本
      textSecondary: '#595959',  // 次要文本
      textTertiary: '#8c8c8c', // 辅助文本
      border: '#e8e8e8',        // 边框
      borderLight: '#f0f0f0',  // 浅边框
      bgLight: '#fafafa',       // 浅背景
      bgContainer: '#f0f2f5',  // 容器背景
      white: '#ffffff'          // 白色
    }

    return (
      <div style={{ background: colors.bgContainer, minHeight: '100vh' }}>
        <PageHeader
          breadcrumbList={breadcrumbList}
        />
        <div className="i2-container service-alerts-list" style={{ padding: '24px', background: colors.bgContainer }}>
          <Card 
            className="i2-card-page"
            style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <Row gutter={[24, 24]}>
              {/* 视频播放区域 */}
              <Col xs={24} lg={18}>
                <Card 
                  title={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '20px', 
                        fontWeight: 600,
                        color: colors.textPrimary,
                        lineHeight: '1.5'
                      }}>
                        视频监控
                      </h3>
                      <p style={{ 
                        margin: 0,
                        fontSize: '12px', 
                        color: colors.textTertiary,
                        fontWeight: 400,
                        lineHeight: '1.5'
                      }}>
                        设备: {deviceSerial || '-'} | 通道: {channelNo || 1}
                      </p>
                    </div>
                  }
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'box-shadow 0.3s ease'
                  }}
                  headStyle={{
                    borderBottom: `1px solid ${colors.borderLight}`,
                    padding: '16px 24px'
                  }}
                >
                  <div
                    ref={this.videoContainerRef}
                    id="video-profile-player"
                    style={{
                      width: '100%',
                      height: '600px',
                      minHeight: '600px',
                      maxHeight: '600px',
                      background: '#000',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      flexShrink: 0,
                      boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ opacity: 0.7 }}>正在加载视频...</div>
                    </div>
                  </div>
                </Card>
              </Col>

              {/* 控制台区域 */}
              <Col xs={24} lg={6}>
                <Card 
                  title={
                    <span style={{ 
                      fontSize: '16px', 
                      fontWeight: 600,
                      color: colors.textPrimary
                    }}>
                      控制面板
                    </span>
                  }
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    height: '100%',
                    transition: 'box-shadow 0.3s ease'
                  }}
                  headStyle={{
                    borderBottom: `1px solid ${colors.borderLight}`,
                    padding: '16px 20px'
                  }}
                  bodyStyle={{
                    padding: '24px'
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {/* 画面控制 */}
                    <div>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        marginBottom: '12px',
                        color: colors.textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <RotateRightOutlined style={{ fontSize: '16px', color: colors.primary }} />
                        画面控制
                      </div>
                      <Button
                        type={isRotated ? 'primary' : 'default'}
                        icon={<RotateRightOutlined />}
                        onClick={this.handleRotateToggle}
                        block
                        disabled={!playerReady}
                        size="large"
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          fontWeight: 500,
                          cursor: playerReady ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (playerReady && !isRotated) {
                            e.currentTarget.style.borderColor = colors.primary
                            e.currentTarget.style.color = colors.primary
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (playerReady && !isRotated) {
                            e.currentTarget.style.borderColor = undefined
                            e.currentTarget.style.color = undefined
                          }
                        }}
                      >
                        {isRotated ? '恢复正常' : '180度翻转'}
                      </Button>
                    </div>

                    {/* 云台控制 */}
                    <div>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        marginBottom: '12px',
                        color: colors.textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <UpOutlined style={{ fontSize: '16px', color: colors.primary }} />
                        云台控制
                      </div>
                      <div style={{
                        background: colors.bgLight,
                        padding: '16px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.borderLight}`
                      }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {/* 上 */}
                          <Button
                            icon={<UpOutlined />}
                            onClick={() => this.handlePTZControl('up')}
                            onMouseUp={this.handlePTZStop}
                            block
                            disabled={!playerReady}
                            style={{
                              height: '40px',
                              borderRadius: '8px',
                              fontWeight: 500,
                              cursor: playerReady ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (playerReady) {
                                e.currentTarget.style.borderColor = colors.primary
                                e.currentTarget.style.color = colors.primary
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (playerReady) {
                                e.currentTarget.style.borderColor = undefined
                                e.currentTarget.style.color = undefined
                              }
                            }}
                          >
                            向上
                          </Button>
                          {/* 左右 */}
                          <Space style={{ width: '100%' }} size="small">
                            <Button
                              icon={<LeftOutlined />}
                              onClick={() => this.handlePTZControl('left')}
                              onMouseUp={this.handlePTZStop}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                cursor: playerReady ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease'
                              }}
                              disabled={!playerReady}
                              onMouseEnter={(e) => {
                                if (playerReady) {
                                  e.currentTarget.style.borderColor = colors.primary
                                  e.currentTarget.style.color = colors.primary
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (playerReady) {
                                  e.currentTarget.style.borderColor = undefined
                                  e.currentTarget.style.color = undefined
                                }
                              }}
                            >
                              向左
                            </Button>
                            <Button
                              icon={<RightOutlined />}
                              onClick={() => this.handlePTZControl('right')}
                              onMouseUp={this.handlePTZStop}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                cursor: playerReady ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease'
                              }}
                              disabled={!playerReady}
                              onMouseEnter={(e) => {
                                if (playerReady) {
                                  e.currentTarget.style.borderColor = colors.primary
                                  e.currentTarget.style.color = colors.primary
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (playerReady) {
                                  e.currentTarget.style.borderColor = undefined
                                  e.currentTarget.style.color = undefined
                                }
                              }}
                            >
                              向右
                            </Button>
                          </Space>
                          {/* 下 */}
                          <Button
                            icon={<DownOutlined />}
                            onClick={() => this.handlePTZControl('down')}
                            onMouseUp={this.handlePTZStop}
                            block
                            disabled={!playerReady}
                            style={{
                              height: '40px',
                              borderRadius: '8px',
                              fontWeight: 500,
                              cursor: playerReady ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (playerReady) {
                                e.currentTarget.style.borderColor = colors.primary
                                e.currentTarget.style.color = colors.primary
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (playerReady) {
                                e.currentTarget.style.borderColor = undefined
                                e.currentTarget.style.color = undefined
                              }
                            }}
                          >
                            向下
                          </Button>
                          {/* 物理缩放（云台控制） */}
                          <div style={{ 
                            marginTop: '8px',
                            paddingTop: '12px',
                            borderTop: `1px solid ${colors.border}`
                          }}>
                            <div style={{ 
                              fontSize: '11px', 
                              color: colors.textTertiary,
                              marginBottom: '8px',
                              textAlign: 'center'
                            }}>
                              物理缩放（硬件）
                            </div>
                            <Space style={{ width: '100%' }} size="small">
                              <Button
                                icon={<ZoomInOutlined />}
                                onClick={() => this.handlePTZControl('zoomIn')}
                                onMouseUp={this.handlePTZStop}
                                style={{ 
                                  flex: 1,
                                  height: '36px',
                                  borderRadius: '8px',
                                  fontWeight: 500,
                                  cursor: playerReady ? 'pointer' : 'not-allowed',
                                  transition: 'all 0.2s ease'
                                }}
                                disabled={!playerReady}
                                onMouseEnter={(e) => {
                                  if (playerReady) {
                                    e.currentTarget.style.borderColor = colors.primary
                                    e.currentTarget.style.color = colors.primary
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (playerReady) {
                                    e.currentTarget.style.borderColor = undefined
                                    e.currentTarget.style.color = undefined
                                  }
                                }}
                              >
                                放大
                              </Button>
                              <Button
                                icon={<ZoomOutOutlined />}
                                onClick={() => this.handlePTZControl('zoomOut')}
                                onMouseUp={this.handlePTZStop}
                                style={{ 
                                  flex: 1,
                                  height: '36px',
                                  borderRadius: '8px',
                                  fontWeight: 500,
                                  cursor: playerReady ? 'pointer' : 'not-allowed',
                                  transition: 'all 0.2s ease'
                                }}
                                disabled={!playerReady}
                                onMouseEnter={(e) => {
                                  if (playerReady) {
                                    e.currentTarget.style.borderColor = colors.primary
                                    e.currentTarget.style.color = colors.primary
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (playerReady) {
                                    e.currentTarget.style.borderColor = undefined
                                    e.currentTarget.style.color = undefined
                                  }
                                }}
                              >
                                缩小
                              </Button>
                            </Space>
                          </div>
                        </Space>
                      </div>
                    </div>

                    {/* 电子放大控制 */}
                    <div>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        marginBottom: '12px',
                        color: colors.textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <ZoomInOutlined style={{ fontSize: '16px', color: colors.primary }} />
                        电子放大
                      </div>
                      <div style={{
                        background: colors.bgLight,
                        padding: '16px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.borderLight}`
                      }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {/* 开启/关闭电子放大 */}
                          <Button
                            type={isZoomEnabled ? 'primary' : 'default'}
                            icon={isZoomEnabled ? <ZoomInOutlined /> : <ZoomOutOutlined />}
                            onClick={isZoomEnabled ? this.handleCloseZoom : this.handleEnableZoom}
                            block
                            disabled={!playerReady}
                            size="large"
                            style={{
                              height: '40px',
                              borderRadius: '8px',
                              fontWeight: 500,
                              cursor: playerReady ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (playerReady && !isZoomEnabled) {
                                e.currentTarget.style.borderColor = colors.primary
                                e.currentTarget.style.color = colors.primary
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (playerReady && !isZoomEnabled) {
                                e.currentTarget.style.borderColor = undefined
                                e.currentTarget.style.color = undefined
                              }
                            }}
                          >
                            {isZoomEnabled ? '关闭电子放大' : '开启电子放大'}
                          </Button>
                          {/* 放大/缩小控制（仅在电子放大开启时显示） */}
                          {isZoomEnabled && (
                            <div style={{ 
                              marginTop: '8px',
                              paddingTop: '12px',
                              borderTop: `1px solid ${colors.border}`
                            }}>
                              <Space style={{ width: '100%' }} size="small">
                                <Button
                                  icon={<ZoomInOutlined />}
                                  onClick={() => this.handleZoomAdd(1)}
                                  style={{ 
                                    flex: 1,
                                    height: '36px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = colors.primary
                                    e.currentTarget.style.color = colors.primary
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = undefined
                                    e.currentTarget.style.color = undefined
                                  }}
                                >
                                  放大
                                </Button>
                                <Button
                                  icon={<ZoomOutOutlined />}
                                  onClick={() => this.handleZoomSub(1)}
                                  style={{ 
                                    flex: 1,
                                    height: '36px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = colors.primary
                                    e.currentTarget.style.color = colors.primary
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = undefined
                                    e.currentTarget.style.color = undefined
                                  }}
                                >
                                  缩小
                                </Button>
                              </Space>
                              {zoomLevel > 0 && (
                                <div style={{ 
                                  textAlign: 'center', 
                                  fontSize: '12px', 
                                  color: colors.textTertiary,
                                  marginTop: '8px',
                                  fontWeight: 500
                                }}>
                                  放大级别: <span style={{ 
                                    color: colors.primary,
                                    fontWeight: 600
                                  }}>
                                    {zoomLevel}x
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </Space>
                      </div>
                    </div>

                    {/* 音量控制 */}
                    <div>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        marginBottom: '12px',
                        color: colors.textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {isSoundOn ? (
                          <SoundFilled style={{ fontSize: '16px', color: colors.primary }} />
                        ) : (
                          <SoundOutlined style={{ fontSize: '16px', color: colors.primary }} />
                        )}
                        音量控制
                      </div>
                      <div style={{
                        background: colors.bgLight,
                        padding: '16px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.borderLight}`
                      }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                          <Button
                            type={isSoundOn ? 'primary' : 'default'}
                            icon={isSoundOn ? <SoundFilled /> : <SoundOutlined />}
                            onClick={this.handleSoundToggle}
                            block
                            disabled={!playerReady}
                            size="large"
                            style={{
                              height: '40px',
                              borderRadius: '8px',
                              fontWeight: 500,
                              cursor: playerReady ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (playerReady && !isSoundOn) {
                                e.currentTarget.style.borderColor = colors.primary
                                e.currentTarget.style.color = colors.primary
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (playerReady && !isSoundOn) {
                                e.currentTarget.style.borderColor = undefined
                                e.currentTarget.style.color = undefined
                              }
                            }}
                          >
                            {isSoundOn ? '关闭声音' : '开启声音'}
                          </Button>
                          <div style={{ padding: '0 4px' }}>
                            <Slider
                              value={volume}
                              onChange={this.handleVolumeChange}
                              min={0}
                              max={100}
                              disabled={!playerReady}
                              tooltip={{ 
                                formatter: (value) => `${value}%`,
                                placement: 'top'
                              }}
                              trackStyle={{
                                background: isSoundOn ? colors.primary : colors.border
                              }}
                              handleStyle={{
                                borderColor: isSoundOn ? colors.primary : colors.border
                              }}
                            />
                            <div style={{ 
                              textAlign: 'center', 
                              fontSize: '12px', 
                              color: colors.textTertiary,
                              marginTop: '8px',
                              fontWeight: 500
                            }}>
                              音量: <span style={{ 
                                color: isSoundOn ? colors.primary : colors.textTertiary,
                                fontWeight: 600
                              }}>
                                {volume}%
                              </span>
                            </div>
                          </div>
                        </Space>
                      </div>
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    );
  }
}

const VideoProfileWrapper = () => {
  const dispatch = useDispatch()
  const props = {
    app: useSelector((state) => state.app),
    dispatch
  };
  // 使用 WithRouter 包装 VideoProfile 以获取 location 等路由信息
  const VideoProfileWithRouter = WithRouter(VideoProfile);
  return <VideoProfileWithRouter {...props} />;
};

export default VideoProfileWrapper;
